'use client';

import { useState, useEffect } from 'react';
import { useTheme } from './ThemeProvider';
import { shouldShowThemeSwitcher } from './themeSwitcher.helpers';

export default function ThemeSwitcher() {
  const { theme, setThemeById, allThemes, pattern, setPatternById, allPatterns } = useTheme();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'colors' | 'patterns'>('colors');
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Read once on mount (no useSearchParams → no Suspense / hydration concerns).
    setVisible(shouldShowThemeSwitcher(window.location.search));
  }, []);

  // Mini pattern preview icons
  const patternIcons: Record<string, string> = {
    none: '⊘',
    waves: '🌊',
    peaks: '⛰️',
    blobs: '🫐',
    circles: '⭕',
    diagonal: '◤',
    dots: '⠿',
    hexagons: '⬡',
    zigzag: '⚡',
    topo: '🗺️',
    aurora: '🌈',
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100]">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white text-xl hover:scale-110 transition-transform"
        style={{ backgroundColor: theme.cta, color: theme.ctaText }}
        aria-label="Switch theme"
      >
        🎨
      </button>

      {/* Theme panel */}
      {open && (
        <div className="absolute bottom-18 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-stone-200 mb-2 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="font-bold text-sm" style={{ color: theme.heading }}>Customize</h3>
            <button
              onClick={() => setOpen(false)}
              className="text-stone-400 hover:text-stone-600 text-lg leading-none"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-stone-200 px-4">
            <button
              onClick={() => setTab('colors')}
              className={`flex-1 py-2 text-xs font-semibold border-b-2 transition-colors ${
                tab === 'colors'
                  ? 'border-stone-800 text-stone-800'
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              Colors
            </button>
            <button
              onClick={() => setTab('patterns')}
              className={`flex-1 py-2 text-xs font-semibold border-b-2 transition-colors ${
                tab === 'patterns'
                  ? 'border-stone-800 text-stone-800'
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              Patterns
            </button>
          </div>

          <div className="p-4 max-h-[420px] overflow-y-auto">
            {tab === 'colors' ? (
              <>
                <p className="text-xs text-stone-500 mb-3">Click to preview. Pick the one you like.</p>
                <div className="grid grid-cols-2 gap-2">
                  {allThemes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setThemeById(t.id)}
                      className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 transition-all text-left ${
                        theme.id === t.id ? 'border-stone-800 bg-stone-50' : 'border-transparent hover:bg-stone-50'
                      }`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden flex flex-col">
                        <div className="flex-1" style={{ backgroundColor: t.primary }} />
                        <div className="h-2" style={{ backgroundColor: t.cta }} />
                      </div>
                      <span className="text-xs font-medium text-stone-700 leading-tight">{t.name}</span>
                    </button>
                  ))}
                </div>

                {/* Current theme info */}
                <div className="mt-4 pt-3 border-t border-stone-100">
                  <p className="text-xs text-stone-500">
                    Active: <span className="font-semibold text-stone-700">{theme.name}</span>
                  </p>
                  <div className="flex gap-1.5 mt-2">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.primary }} title="Primary" />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.primaryLight }} title="Primary Light" />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.cta }} title="CTA" />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.surface }} title="Surface" />
                    <div className="w-6 h-6 rounded border border-stone-200" style={{ backgroundColor: theme.heading }} title="Heading" />
                    <div className="w-6 h-6 rounded border border-stone-200" style={{ backgroundColor: theme.body }} title="Body" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-stone-500 mb-3">Add decorative patterns to sections. Works with any color scheme.</p>
                <div className="grid grid-cols-2 gap-2">
                  {allPatterns.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPatternById(p.id)}
                      className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 transition-all text-left ${
                        pattern.id === p.id ? 'border-stone-800 bg-stone-50' : 'border-transparent hover:bg-stone-50'
                      }`}
                    >
                      {/* Pattern preview swatch */}
                      <div
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base"
                        style={{ backgroundColor: p.id === 'none' ? '#F5F5F5' : theme.primary }}
                      >
                        <span className={p.id === 'none' ? '' : 'brightness-200 contrast-200'} style={{ filter: p.id === 'none' ? undefined : 'brightness(2)' }}>
                          {patternIcons[p.id] || '✦'}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-stone-700 leading-tight">{p.name}</span>
                    </button>
                  ))}
                </div>

                {/* Current pattern info */}
                <div className="mt-4 pt-3 border-t border-stone-100">
                  <p className="text-xs text-stone-500">
                    Active pattern: <span className="font-semibold text-stone-700">{pattern.name}</span>
                  </p>
                  <p className="text-xs text-stone-400 mt-1">
                    Active color: <span className="font-medium text-stone-500">{theme.name}</span>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
